import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/App';
import { useConfig } from '@/context/ConfigContext';
import { useApiData, useRefreshableData } from '@/hooks';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadialBarChart, RadialBar
} from 'recharts';
import { 
  Activity, Users, AlertTriangle, TrendingUp, TrendingDown, 
  ClipboardCheck, Heart, Shield, Lightbulb, RefreshCw,
  ArrowUpRight, ArrowDownRight, Minus, Sparkles, ChevronRight,
  ClipboardList, Stethoscope, AlertCircle, Clock, Calendar,
  FileCheck, UserMinus, DollarSign, Building, Gauge,
  Eye, EyeOff, Bell, HeartPulse, UserPlus, Target, Award, Zap, PieChart as PieChartIcon, LayoutDashboard
} from 'lucide-react';
import HVLDLTrends from '@/components/HVLDLTrends';
import HistoricalDataFilter from '@/components/HistoricalDataFilter';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import { EnhancedReportSection } from '@/components/EnhancedReportSection';
import DataFixToolbar from '@/components/common/DataFixToolbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compliance Alert Banner Component
const ComplianceBanner = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  
  const getAlertStyle = (type) => {
    switch (type) {
      case 'critical': return 'compliance-banner-critical';
      case 'warning': return 'compliance-banner-warning';
      default: return 'compliance-banner-info';
    }
  };
  
  const getIcon = (type) => {
    switch (type) {
      case 'critical': return <AlertTriangle className="h-5 w-5" />;
      case 'warning': return <Clock className="h-5 w-5" />;
      default: return <Bell className="h-5 w-5" />;
    }
  };
  
  return (
    <div className="space-y-2 mb-6">
      {alerts.slice(0, 3).map((alert, i) => (
        <div key={i} className={`compliance-banner ${getAlertStyle(alert.type)}`}>
          {getIcon(alert.type)}
          <div className="flex-1">
            <span className="font-semibold">{alert.title}</span>
            <span className="mx-2">—</span>
            <span className="text-sm opacity-80">{alert.message}</span>
          </div>
          {alert.action && (
            <Link to={alert.href || '#'}>
              <Button variant="ghost" size="sm" className="shrink-0">
                {alert.action}
              </Button>
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};

// Stat Card with Futuristic Neon Design
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, status, size = 'default', className = '', color = 'cyan' }) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <ArrowUpRight className="h-4 w-4" />;
    if (trend === 'down') return <ArrowDownRight className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };
  
  const getTrendColor = () => {
    if (status === 'good') return trend === 'up' ? 'text-emerald-400' : 'text-rose-400';
    if (status === 'bad') return trend === 'up' ? 'text-rose-400' : 'text-emerald-400';
    return 'text-slate-400';
  };

  const colorMap = {
    cyan: { hex: '#22d3ee', glow: 'rgba(34, 211, 238, 0.3)' },
    emerald: { hex: '#34d399', glow: 'rgba(52, 211, 153, 0.3)' },
    violet: { hex: '#a78bfa', glow: 'rgba(167, 139, 250, 0.3)' },
    amber: { hex: '#fbbf24', glow: 'rgba(251, 191, 36, 0.3)' },
    rose: { hex: '#fb7185', glow: 'rgba(251, 113, 133, 0.3)' },
    blue: { hex: '#3b82f6', glow: 'rgba(59, 130, 246, 0.3)' }
  };
  
  const c = colorMap[color] || colorMap.cyan;

  return (
    <Card 
      className={`overflow-hidden bg-slate-900/80 backdrop-blur-xl border transition-all duration-500 hover:-translate-y-1 hover:scale-[1.02] group ${className}`}
      style={{ 
        borderColor: c.hex + '40',
        boxShadow: `0 0 25px ${c.glow}`
      }}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Animated gradient background */}
      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity" style={{ background: `linear-gradient(135deg, ${c.hex}30 0%, transparent 50%)` }} />
      
      <CardContent className={size === 'compact' ? 'p-4' : 'pt-6'}>
        <div className="flex items-start justify-between relative z-10">
          <div className="space-y-1">
            <p className="text-sm font-bold text-slate-400 uppercase tracking-wide">{title}</p>
            <p 
              className={`font-black tracking-tight text-white ${size === 'compact' ? 'text-3xl' : 'text-4xl'}`}
              style={{ textShadow: `0 0 20px ${c.glow}` }}
            >
              {value}
            </p>
            {subtitle && <p className="text-xs text-slate-500 font-medium">{subtitle}</p>}
          </div>
          <div 
            className={`rounded-2xl flex items-center justify-center backdrop-blur-sm ${size === 'compact' ? 'h-12 w-12' : 'h-14 w-14'}`}
            style={{ 
              backgroundColor: c.hex + '15',
              boxShadow: `0 0 15px ${c.glow}`
            }}
          >
            <Icon 
              className={`${size === 'compact' ? 'h-6 w-6' : 'h-7 w-7'}`}
              style={{ color: c.hex, filter: `drop-shadow(0 0 8px ${c.hex})` }}
            />
          </div>
        </div>
        {trendValue && (
          <div className={`flex items-center gap-1 mt-3 text-sm font-bold ${getTrendColor()}`}>
            {getTrendIcon()}
            <span>{trendValue}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Predictive Panel Component with Futuristic Design
const PredictivePanel = ({ title, icon: Icon, value, risk, description, trend }) => {
  const riskConfig = {
    high: { color: '#fb7185', glow: 'rgba(251, 113, 133, 0.3)', badge: 'bg-gradient-to-r from-rose-500 to-red-600' },
    medium: { color: '#fbbf24', glow: 'rgba(251, 191, 36, 0.3)', badge: 'bg-gradient-to-r from-amber-400 to-orange-500' },
    low: { color: '#34d399', glow: 'rgba(52, 211, 153, 0.3)', badge: 'bg-gradient-to-r from-emerald-400 to-teal-500' }
  };
  
  const config = riskConfig[risk] || riskConfig.low;
  
  const getRiskBadge = () => {
    const labels = { high: 'High Risk', medium: 'Medium', low: 'Low Risk' };
    return (
      <span 
        className={`px-3 py-1 rounded-full text-xs font-bold text-white ${config.badge}`}
        style={{ boxShadow: `0 0 15px ${config.glow}` }}
      >
        {labels[risk] || 'Low Risk'}
      </span>
    );
  };
  
  return (
    <div 
      className="relative overflow-hidden p-5 rounded-2xl bg-slate-900/60 backdrop-blur-xl border transition-all duration-300 hover:-translate-y-1"
      style={{ 
        borderColor: config.color + '40',
        boxShadow: `0 0 20px ${config.glow}`
      }}
      data-testid={`predictive-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Background glow */}
      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${config.color} 0%, transparent 70%)` }} />
      
      <div className="flex items-start justify-between mb-3 relative z-10">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" style={{ color: config.color, filter: `drop-shadow(0 0 5px ${config.color})` }} />
          <span className="font-bold text-sm text-white">{title}</span>
        </div>
        {getRiskBadge()}
      </div>
      <div className="text-3xl font-black text-white relative z-10" style={{ textShadow: `0 0 15px ${config.glow}` }}>{value}</div>
      <p className="text-sm text-slate-400 font-medium mt-1 relative z-10">{description}</p>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs text-slate-500 font-medium relative z-10">
          <TrendingUp className="h-3 w-3" />
          {trend}
        </div>
      )}
    </div>
  );
};

// Visit Expectation Gauge (for RNCM view)
const VisitGauge = ({ current, target, max }) => {
  const percentage = Math.min((current / max) * 100, 100);
  const isInRange = current >= target.min && current <= target.max;
  
  return (
    <Card className="stat-card" data-testid="visit-gauge">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Visit Expectation</h3>
          <Badge className={isInRange ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
            {isInRange ? 'On Target' : 'Review Needed'}
          </Badge>
        </div>
        <div className="relative pt-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-3xl font-bold">{current}</span>
            <span className="text-sm text-muted-foreground">Target: {target.min}-{target.max}</span>
          </div>
          <div className="relative h-4 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            {/* Target zone indicator */}
            <div 
              className="absolute h-full bg-emerald-200 dark:bg-emerald-900/50"
              style={{ 
                left: `${(target.min / max) * 100}%`, 
                width: `${((target.max - target.min) / max) * 100}%` 
              }}
            />
            {/* Current progress */}
            <div 
              className={`h-full rounded-full transition-all duration-500 ${isInRange ? 'bg-emerald-500' : percentage > 90 ? 'bg-red-500' : 'bg-amber-500'}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>0</span>
            <span>{max}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Quality Score Radial
const QualityScoreGauge = ({ score }) => {
  const getScoreConfig = (s) => {
    if (s >= 90) return { color: '#059669', label: 'Excellent', bg: 'from-emerald-50 to-white dark:from-emerald-900/20' };
    if (s >= 75) return { color: '#0d9488', label: 'Good', bg: 'from-teal-50 to-white dark:from-teal-900/20' };
    if (s >= 60) return { color: '#d97706', label: 'Fair', bg: 'from-amber-50 to-white dark:from-amber-900/20' };
    return { color: '#dc2626', label: 'Needs Attention', bg: 'from-red-50 to-white dark:from-red-900/20' };
  };
  
  const config = getScoreConfig(score);
  const data = [{ value: score, fill: config.color }];

  return (
    <Card className="overflow-hidden bg-white dark:bg-slate-900 relative" data-testid="quality-score-card">
      <div className={`absolute inset-0 bg-gradient-to-br ${config.bg}`} />
      <CardHeader className="relative pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Quality Score
        </CardTitle>
      </CardHeader>
      <CardContent className="relative">
        <div className="flex flex-col items-center">
          <div style={{ position: 'relative', width: '144px', height: '144px' }}>
            <ResponsiveContainer width={144} height={144}>
              <RadialBarChart 
                cx="50%" 
                cy="50%" 
                innerRadius="70%" 
                outerRadius="100%" 
                data={data} 
                startAngle={90} 
                endAngle={-270}
              >
                <RadialBar
                  background={{ fill: 'hsl(var(--muted))' }}
                  dataKey="value"
                  cornerRadius={10}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold" style={{ color: config.color }}>
                {score}
              </span>
              <span className="text-xs text-muted-foreground">/100</span>
            </div>
          </div>
          <Badge 
            className="mt-2 px-3 py-1" 
            style={{ backgroundColor: config.color + '20', color: config.color }}
          >
            {config.label}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Alert Card
const AlertCard = ({ alerts }) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical': return { color: 'bg-red-500', ring: 'ring-red-500/30', text: 'text-red-700 dark:text-red-400' };
      case 'high': return { color: 'bg-amber-500', ring: 'ring-amber-500/30', text: 'text-amber-700 dark:text-amber-400' };
      case 'medium': return { color: 'bg-yellow-500', ring: 'ring-yellow-500/30', text: 'text-yellow-700 dark:text-yellow-400' };
      default: return { color: 'bg-blue-500', ring: 'ring-blue-500/30', text: 'text-blue-700 dark:text-blue-400' };
    }
  };

  if (!alerts || alerts.length === 0) {
    return (
      <Card data-testid="alerts-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-600" />
            Active Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
              <Shield className="h-7 w-7 text-emerald-600" />
            </div>
            <p className="font-semibold text-emerald-600 dark:text-emerald-400">All Clear</p>
            <p className="text-sm text-muted-foreground mt-1">No threshold breaches detected</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="alerts-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Active Alerts
          </CardTitle>
          <Badge variant="destructive">{alerts.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {alerts.slice(0, 4).map((alert, index) => {
          const config = getSeverityConfig(alert.severity);
          return (
            <div 
              key={index} 
              className={`flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 animate-fade-in`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className={`h-2.5 w-2.5 rounded-full mt-1.5 ${config.color}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${config.text}`}>{alert.metric}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{alert.message}</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Quick Action Card
const QuickActionCard = ({ title, description, icon: Icon, href }) => (
  <Link to={href}>
    <Card className="stat-card group cursor-pointer h-full">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{title}</h3>
            <p className="text-xs text-muted-foreground truncate">{description}</p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

export default function Dashboard() {
  const { token, user } = useAuth();
  const { getWorkforceTarget, getStrategicTarget } = useConfig();
  
  // Get configurable targets from context
  const visitTargets = useMemo(() => ({
    min: getWorkforceTarget('optimal_visits_per_week', 18) - 3,
    max: getWorkforceTarget('optimal_visits_per_week', 18) + 2
  }), [getWorkforceTarget]);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [roleViewData, setRoleViewData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [viewMode, setViewMode] = useState('overview'); // overview, rncm, admissions, executive
  const [auditMode, setAuditMode] = useState(false);
  const [programLastUpdated, setProgramLastUpdated] = useState(null);

  // Fetch role-specific data when view mode changes
  const fetchRoleViewData = async (view) => {
    if (view === 'overview') {
      setRoleViewData(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/dashboard/role-view/${view}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRoleViewData(data);
      }
    } catch (error) {
      console.error('Failed to fetch role view data:', error);
    }
  };

  // Compliance alerts from real data
  const complianceAlerts = dashboardData?.alerts?.recent?.slice(0, 2).map(a => ({
    type: a.severity === 'critical' ? 'critical' : 'warning',
    title: a.title || 'Alert',
    message: a.message || a.description,
    action: 'View',
    href: '/alerts'
  })) || [];

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      const [summaryRes, alertsRes, programRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/metrics/program-overview/latest`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (summaryRes.ok) setDashboardData(await summaryRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (programRes.ok) {
        const programData = await programRes.json();
        if (programData?.created_at) {
          setProgramLastUpdated(programData.created_at);
        }
      }
      
      // Also fetch role view data if not on overview
      if (viewMode !== 'overview') {
        fetchRoleViewData(viewMode);
      }
      
      if (isRefresh) toast.success('Dashboard refreshed');
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error(isRefresh ? 'Failed to refresh dashboard' : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [token]);
  
  // Fetch role data when view mode changes
  useEffect(() => {
    fetchRoleViewData(viewMode);
  }, [viewMode]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData(true);
  };

  // Format relative time for last updated
  const formatLastUpdated = (isoDate) => {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Trend chart data - fetched from API
  const [trendData, setTrendData] = useState([]);
  const [disciplineData, setDisciplineData] = useState([]);
  const [hasRealTrendData, setHasRealTrendData] = useState(false);
  const [hasRealDisciplineData, setHasRealDisciplineData] = useState(false);

  // Fetch trend and discipline data
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
        
        // Fetch trend data
        const trendRes = await fetch(`${API_URL}/api/dashboard/charts/trend-data?weeks=4`, { headers });
        if (trendRes.ok) {
          const trendJson = await trendRes.json();
          setTrendData(trendJson.trend_data || []);
          setHasRealTrendData(trendJson.has_real_data);
        }
        
        // Fetch discipline breakdown
        const discRes = await fetch(`${API_URL}/api/dashboard/charts/discipline-breakdown`, { headers });
        if (discRes.ok) {
          const discJson = await discRes.json();
          setDisciplineData(discJson.discipline_data || []);
          setHasRealDisciplineData(discJson.has_real_data);
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
        // Fallback to default data
        setTrendData([
          { name: 'W1', adc: dashboardData?.program_overview?.adc || 50, admissions: 12, discharges: 8 },
          { name: 'W2', adc: dashboardData?.program_overview?.adc || 50, admissions: 15, discharges: 10 },
          { name: 'W3', adc: dashboardData?.program_overview?.adc || 50, admissions: 18, discharges: 14 },
          { name: 'W4', adc: dashboardData?.program_overview?.adc || 50, admissions: dashboardData?.program_overview?.total_admissions || 14, discharges: 12 }
        ]);
        setDisciplineData([
          { name: 'RN', value: 12, color: '#0f172a' },
          { name: 'LPN', value: 4, color: '#0d9488' },
          { name: 'SW', value: 3, color: '#d97706' },
          { name: 'Aide', value: 8, color: '#6366f1' },
          { name: 'Chaplain', value: 2, color: '#8b5cf6' },
        ]);
      }
    };
    
    if (token && !loading) {
      fetchChartData();
    }
  }, [token, loading, dashboardData]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${auditMode ? 'audit-ready-mode rounded-xl p-2' : ''}`} data-testid="dashboard">
      {/* Live Compliance Banner */}
      <ComplianceBanner alerts={complianceAlerts} />
      
      {/* Futuristic Neon Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-violet-600 to-fuchsia-600 animate-gradient-x" />
        
        {/* Mesh overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
                <LayoutDashboard className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
              </div>
              <span className="drop-shadow-lg" style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>
                Welcome back, {user?.name?.split(' ')[0] || 'User'}
              </span>
            </h1>
            <p className="text-white/80 mt-3 text-lg font-medium max-w-xl">
              Your QAPI operations dashboard - Real-time metrics and insights
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Selector */}
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-[160px] bg-white/10 border-white/20 text-white backdrop-blur-sm font-bold" data-testid="view-mode-select">
                <SelectValue placeholder="Select view" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="overview">Overview</SelectItem>
                <SelectItem value="rncm">RN Case Manager</SelectItem>
                <SelectItem value="admissions">Admissions Director</SelectItem>
                <SelectItem value="executive">Executive View</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Audit Ready Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
              <Switch 
                id="audit-mode" 
                checked={auditMode} 
                onCheckedChange={setAuditMode}
                data-testid="audit-mode-toggle"
              />
              <Label htmlFor="audit-mode" className="text-white cursor-pointer font-bold">
                {auditMode ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Label>
            </div>
            
            <Button 
              onClick={handleRefresh} 
              disabled={refreshing} 
              variant="secondary" 
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm font-bold gap-2 transition-all hover:scale-105"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Dashboard Content - Changes based on viewMode */}
      {viewMode === 'overview' && (
        <>
          {/* Data Fix Toolbar */}
          <DataFixToolbar 
            onDataFixed={handleRefresh} 
            variant="compact"
            className="mb-2"
          />
          
          {/* Key Stats Grid with Futuristic Neon Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Average Daily Census"
              value={dashboardData?.program_overview?.adc?.toFixed(1) || '50.0'}
              subtitle="Current ADC"
              icon={Users}
              color="cyan"
              trend="up"
              trendValue="+3.2% this month"
              status="good"
            />
            <StatCard
              title="Quality Score"
              value={`${dashboardData?.clinical_quality?.pain_controlled_48h_percent || 92}%`}
              subtitle="Pain Controlled 48h"
              icon={Heart}
              color="emerald"
              trend="up"
              trendValue="+2.1%"
              status="good"
            />
            <StatCard
              title="Active PIPs"
              value={dashboardData?.pips?.active || 0}
              subtitle={`${dashboardData?.pips?.pending || 0} pending`}
              icon={ClipboardList}
              color="amber"
            />
            <StatCard
              title="Compliance"
              value={`${dashboardData?.compliance?.overall_rating || 95}%`}
              subtitle={dashboardData?.compliance?.survey_ready ? "Survey Ready" : "Review Needed"}
              icon={Shield}
              color="violet"
              status="good"
            />
          </div>

          {/* Program Metrics Update Indicator */}
          {programLastUpdated && (
            <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-900/60 backdrop-blur-xl border border-slate-700/50" data-testid="metrics-update-indicator" style={{ boxShadow: '0 0 15px rgba(34, 211, 238, 0.1)' }}>
              <div className="flex items-center gap-2 text-sm text-slate-400 font-medium">
                <Clock className="h-4 w-4 text-cyan-400" />
                <span>Program metrics last updated: <span className="font-bold text-white">{formatLastUpdated(programLastUpdated)}</span></span>
              </div>
              <Link to="/admissions-discharges">
                <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 font-bold">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Update Now
                </Button>
              </Link>
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Trend Chart */}
            <ModernCard className="lg:col-span-2" data-testid="trend-chart-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <SectionHeader icon={TrendingUp} title="Census & Admissions Trend" />
                  <div className="flex items-center gap-2">
                    {hasRealTrendData ? (
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse inline-block" />
                        Live Data
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Projected
                      </Badge>
                    )}
                    <Link to="/admissions-discharges">
                      <Button variant="ghost" size="sm" className="h-7 text-xs">
                        Update Data
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: '256px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="colorAdc" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorAdm" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                      <XAxis dataKey="name" className="text-xs" axisLine={false} tickLine={false} />
                      <YAxis className="text-xs" axisLine={false} tickLine={false} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '0.75rem',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }} 
                      />
                      <Legend />
                      <Area type="monotone" dataKey="adc" name="ADC" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#colorAdc)" />
                      <Area type="monotone" dataKey="admissions" name="Admissions" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorAdm)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </ModernCard>

            {/* Quality Score */}
            <QualityScoreGauge score={dashboardData?.clinical_quality?.pain_controlled_48h_percent || 92} />
          </div>

          {/* Predictive Panels Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PredictivePanel
              title="RN Burnout Risk"
              icon={HeartPulse}
              value="12%"
              risk="low"
              description="2 staff showing early signs"
              trend="Stable vs last week"
            />
            <PredictivePanel
              title="Hiring Projection"
              icon={UserPlus}
              value="+2 FTE"
              risk="medium"
              description="Needed in 4-6 weeks"
              trend="Based on ADC growth"
            />
            <PredictivePanel
              title="Compliance Risk"
              icon={Shield}
              value="3 Items"
              risk="medium"
              description="Require attention this week"
            />
            <PredictivePanel
              title="Doc Denial Risk"
              icon={FileCheck}
              value="Low"
              risk="low"
              description="All CTIs current"
            />
          </div>

          {/* Quick Actions & Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <SectionHeader icon={Sparkles} title="Quick Actions" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <QuickActionCard
                  title="Enter Weekly Data"
                  description="Record quality metrics"
                  icon={Stethoscope}
                  href="/data-entry"
                />
                <QuickActionCard
                  title="Survey Readiness"
                  description="Check compliance"
                  icon={ClipboardCheck}
                  href="/survey-readiness"
                />
                <QuickActionCard
                  title="AI Insights"
                  description="Get recommendations"
                  icon={Lightbulb}
                  href="/insights"
                />
                <QuickActionCard
                  title="CHAP Readiness"
                  description="Audit risk score"
                  icon={Award}
                  href="/chap-readiness"
                />
              </div>
            </div>
            
            <AlertCard alerts={alerts} />
          </div>

          {/* HVLDL Trends Section */}
          <HVLDLTrends />

          {/* Enhanced Report Section */}
          <EnhancedReportSection 
            domain="all"
            title="Daily Operations Report"
            description="Comprehensive overview of all key metrics with AI-powered insights"
            defaultTimeframe="7d"
            showExport={true}
          />
        </>
      )}

      {/* RN Case Manager View */}
      {viewMode === 'rncm' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard
              title="Your Caseload"
              value={roleViewData?.caseload?.your_patients || dashboardData?.productivity?.total_clinicians || 14}
              subtitle={`Optimal: ${roleViewData?.caseload?.optimal_range || '12-15'} patients`}
              icon={Users}
              trend={roleViewData?.caseload?.status === 'optimal' ? 'stable' : roleViewData?.caseload?.status === 'high' ? 'up' : 'down'}
              trendValue={roleViewData?.caseload?.status === 'optimal' ? 'Optimal range' : roleViewData?.caseload?.status === 'high' ? 'Above optimal' : 'Below optimal'}
              status={roleViewData?.caseload?.status === 'optimal' ? 'good' : 'bad'}
            />
            <VisitGauge 
              current={roleViewData?.visits?.weekly_visits || 17} 
              target={visitTargets} 
              max={25} 
            />
            <StatCard
              title="Overdue Documentation"
              value={roleViewData?.documentation?.overdue_count || 0}
              subtitle={`CTI: ${roleViewData?.documentation?.cti_compliance || 95}%, F2F: ${roleViewData?.documentation?.f2f_compliance || 90}%`}
              icon={AlertCircle}
              trend={roleViewData?.documentation?.trend === 'improving' ? 'down' : 'up'}
              trendValue={roleViewData?.documentation?.trend === 'improving' ? 'Improving' : 'Needs attention'}
              status={roleViewData?.documentation?.overdue_count > 2 ? 'bad' : 'good'}
            />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Upcoming Recertifications
                </CardTitle>
                <CardDescription>
                  {roleViewData?.recertifications?.total_pending || 0} total pending
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                    <span className="font-medium">Due within 7 days</span>
                    <Badge className="bg-red-100 text-red-700">
                      {roleViewData?.recertifications?.upcoming_7_days || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                    <span className="font-medium">Due within 14 days</span>
                    <Badge className="bg-amber-100 text-amber-700">
                      {roleViewData?.recertifications?.upcoming_14_days || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span className="font-medium">Total Pending</span>
                    <Badge className="bg-slate-100 text-slate-700">
                      {roleViewData?.recertifications?.total_pending || 0}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <HeartPulse className="h-5 w-5" />
                  Hospitalization Risk
                </CardTitle>
                <CardDescription>
                  Rate: {roleViewData?.hospitalization_risk?.rate || 8}% (Benchmark: {roleViewData?.hospitalization_risk?.benchmark || 10}%)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">High Risk Patients</span>
                      <Badge className="bg-red-100 text-red-700">
                        {roleViewData?.hospitalization_risk?.high_risk_patients || 0}
                      </Badge>
                    </div>
                    <Progress value={80} className="h-2 bg-red-100" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Medium Risk Patients</span>
                      <Badge className="bg-amber-100 text-amber-700">
                        {roleViewData?.hospitalization_risk?.medium_risk_patients || 0}
                      </Badge>
                    </div>
                    <Progress value={50} className="h-2 bg-amber-100" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Team: {roleViewData?.team_summary?.total_clinicians || 0} clinicians, {roleViewData?.team_summary?.over_capacity || 0} over capacity
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Admissions Director View */}
      {viewMode === 'admissions' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Conversion Ratio"
              value={`${roleViewData?.conversion?.ratio || 72}%`}
              subtitle={`Benchmark: ${roleViewData?.conversion?.benchmark || 70}%`}
              icon={Target}
              trend={roleViewData?.conversion?.trend || 'up'}
              trendValue={roleViewData?.conversion?.trend_value || '+5% vs last month'}
              status={roleViewData?.conversion?.ratio >= 70 ? 'good' : 'bad'}
              size="compact"
            />
            <StatCard
              title="Pending Referrals"
              value={roleViewData?.referrals?.pending || 0}
              subtitle={`${roleViewData?.referrals?.new_today || 0} new today`}
              icon={Clock}
              trend="up"
              trendValue={`${roleViewData?.referrals?.total_week || 0} this week`}
              size="compact"
            />
            <StatCard
              title="Same-Day Admits"
              value={roleViewData?.admissions?.same_day || 0}
              subtitle={`${roleViewData?.admissions?.this_week || 0} total this week`}
              icon={Zap}
              trend="stable"
              trendValue={`Target: ${roleViewData?.admissions?.target || '< 3 days'}`}
              status="good"
              size="compact"
            />
            <StatCard
              title="Insurance Pending"
              value={roleViewData?.insurance?.pending_verification || 0}
              subtitle={`${roleViewData?.insurance?.denials_this_month || 0} denials this month`}
              icon={Shield}
              size="compact"
            />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Facility Pipeline
                </CardTitle>
                <CardDescription>
                  Current census: {roleViewData?.census_impact?.current_census || dashboardData?.program_overview?.adc || 178}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: '256px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={roleViewData?.facility_pipeline || [
                      { name: 'Hospital System A', referrals: 12, admissions: 8 },
                      { name: 'SNF Network', referrals: 8, admissions: 6 },
                      { name: 'Home Health', referrals: 5, admissions: 4 },
                      { name: 'Physician', referrals: 4, admissions: 3 },
                      { name: 'Other', referrals: 2, admissions: 1 },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" className="text-xs" />
                      <YAxis className="text-xs" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="referrals" name="Referrals" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="admissions" name="Admissions" fill="#0f172a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Census Impact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span className="font-medium">Current Census</span>
                    <span className="text-2xl font-bold">{roleViewData?.census_impact?.current_census || dashboardData?.program_overview?.adc || 178}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <span className="font-medium">Projected Weekly Impact</span>
                    <span className={`text-lg font-semibold ${(roleViewData?.census_impact?.projected_impact || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(roleViewData?.census_impact?.projected_impact || 0) >= 0 ? '+' : ''}{roleViewData?.census_impact?.projected_impact || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <span className="font-medium">Appeal Success Rate</span>
                    <span className="text-lg font-semibold">{roleViewData?.insurance?.appeal_success_rate || 75}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Historical Data Trends with Date Filtering */}
          <HistoricalDataFilter />
        </>
      )}

      {/* Executive View */}
      {viewMode === 'executive' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Current Census"
              value={roleViewData?.census?.current_adc || dashboardData?.program_overview?.adc?.toFixed(0) || '178'}
              subtitle={`Target: ${roleViewData?.census?.target || 200}`}
              icon={TrendingUp}
              trend={roleViewData?.census?.trend || 'up'}
              trendValue={roleViewData?.census?.trend_value || '+8% YTD'}
              status="good"
              size="compact"
            />
            <StatCard
              title="Revenue (Weekly)"
              value={`$${((roleViewData?.financial?.weekly_revenue || 187000) / 1000).toFixed(0)}K`}
              subtitle={`Target: $${((roleViewData?.financial?.target_weekly || 200000) / 1000).toFixed(0)}K`}
              icon={DollarSign}
              trend="up"
              trendValue={`Margin: ${roleViewData?.financial?.net_margin || 12.5}%`}
              status="good"
              size="compact"
            />
            <StatCard
              title="PRN Utilization"
              value={`${roleViewData?.staffing?.prn_utilization || 9.2}%`}
              subtitle={`Target: <${roleViewData?.staffing?.target_prn || 12}%`}
              icon={Users}
              trend="down"
              trendValue={`${roleViewData?.staffing?.fte_count || 14} FTE`}
              status="good"
              size="compact"
            />
            <StatCard
              title="Audit Risk Score"
              value={`${roleViewData?.quality?.audit_risk_score || 18}/100`}
              subtitle={`${roleViewData?.quality?.risk_level || 'Low'} risk`}
              icon={Shield}
              trend="stable"
              trendValue={`Compliance: ${roleViewData?.quality?.compliance_score || 95}%`}
              status={roleViewData?.quality?.risk_level === 'low' ? 'good' : 'bad'}
              size="compact"
            />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Productivity Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gauge className="h-5 w-5" />
                  Productivity Overview
                </CardTitle>
                <CardDescription>
                  {roleViewData?.productivity?.over_capacity_count || 0} clinicians over capacity
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                    <div className="text-3xl font-bold">{roleViewData?.productivity?.visits_per_clinician?.toFixed(1) || 17.2}</div>
                    <div className="text-sm text-muted-foreground">Avg Visits/Clinician</div>
                  </div>
                  <div className={`p-4 rounded-xl text-center ${roleViewData?.productivity?.burnout_risk_count > 2 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                    <div className="text-3xl font-bold">{roleViewData?.productivity?.burnout_risk_count || 0}</div>
                    <div className="text-sm text-muted-foreground">Burnout Risk</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Productivity Index</span>
                    <span className="font-semibold">{roleViewData?.staffing?.productivity_index || 85}%</span>
                  </div>
                  <Progress value={roleViewData?.staffing?.productivity_index || 85} className="h-2" />
                </div>
              </CardContent>
            </Card>
            
            {/* Strategic Indicators */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Strategic Indicators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span className="font-medium">Market Growth</span>
                    <Badge className="bg-emerald-100 text-emerald-700">{roleViewData?.strategic?.market_growth || '+12%'}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span className="font-medium">Referral Trend</span>
                    <Badge className={roleViewData?.strategic?.referral_trend === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                      {roleViewData?.strategic?.referral_trend === 'up' ? 'Growing' : 'Stable'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span className="font-medium">Competitor Impact</span>
                    <Badge className={roleViewData?.strategic?.competitor_impact === 'low' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {roleViewData?.strategic?.competitor_impact || 'Low'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <span className="font-medium">Cost per Patient</span>
                    <span className="font-semibold">${roleViewData?.financial?.cost_per_patient?.toLocaleString() || '4,800'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Discipline Distribution */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Staff Distribution by Discipline
                </CardTitle>
                <div className="flex items-center gap-2">
                  {hasRealDisciplineData ? (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse inline-block" />
                      Live Data
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      Default Ratios
                    </Badge>
                  )}
                </div>
              </div>
              <CardDescription className="text-xs">
                {hasRealDisciplineData 
                  ? `${disciplineData.reduce((sum, d) => sum + d.value, 0)} total staff across all disciplines` 
                  : 'Add staff data in System Config to show actual breakdown'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: '192px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={disciplineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {disciplineData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Audit Mode Documentation Links */}
      {auditMode && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Audit-Ready Documentation
            </CardTitle>
            <CardDescription>Quick access to supporting documentation for survey preparation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Eligibility Checklist', href: '/survey-readiness' },
                { name: 'CTI Documentation', href: '/clinical-quality' },
                { name: 'Compliance Reports', href: '/compliance-dashboard' },
                { name: 'PIP Progress', href: '/pips' },
              ].map((doc) => (
                <Link key={doc.name} to={doc.href}>
                  <Button variant="outline" className="w-full audit-link justify-start">
                    <FileCheck className="h-4 w-4 mr-2" />
                    {doc.name}
                  </Button>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
